import { createContext, PropsWithChildren, useContext } from 'react'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import type { ReactDatePickerCustomHeaderProps } from 'react-datepicker'
import { format } from 'libs/dates'
import { useTranslation } from 'libs/i18n'
import { WithClassName } from 'shared/types/general.types'
import { cn } from 'utils/style'
import { DATE_FORMAT, getDateLocale } from '../utils/date'

type CalendarContextValue = {
	summary: string
	actionLabel: string
	isActionDisabled?: boolean
	isRange?: boolean
	onAction: () => void
	onClear: () => void
}

const CalendarContext = createContext<CalendarContextValue | null>(null)

export const CalendarProvider = CalendarContext.Provider

const navButtonClassName =
	'grid h-9 w-9 place-items-center rounded-md text-foreground transition-colors hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:pointer-events-none disabled:opacity-50 [&>svg]:h-4 [&>svg]:w-4 [&>svg]:stroke-2'

const CalendarContainer = ({ className, children }: PropsWithChildren<WithClassName>) => {
	const { t } = useTranslation()
	const context = useContext(CalendarContext)

	if (!context) return <div className={className}>{children}</div>

	const {
		summary,
		actionLabel,
		isActionDisabled = false,
		isRange = false,
		onAction,
		onClear
	} = context

	return (
		<div className={cn(className, 'date-picker', isRange && 'date-picker--range')}>
			<div className="w-0 min-w-full border-b border-border bg-muted/40 px-4 py-3">
				<p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
					{t('Selected')}
				</p>
				<p className="font-display text-base font-semibold text-foreground first-letter:uppercase">
					{summary}
				</p>
			</div>
			{children}
			<div className="flex items-center justify-between border-t border-border px-3 py-2">
				<button
					type="button"
					className="rounded-md px-2 py-1 text-[12px] font-medium text-muted-foreground transition hover:bg-muted hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
					onClick={onClear}>
					{t('Clear')}
				</button>
				<button
					type="button"
					className="rounded-md bg-primary/10 px-3 py-1 text-[12px] font-semibold text-primary transition hover:bg-primary/[0.15] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 disabled:pointer-events-none disabled:opacity-50"
					disabled={isActionDisabled}
					onClick={onAction}>
					{actionLabel}
				</button>
			</div>
		</div>
	)
}

const CalendarHeader = ({
	date,
	decreaseMonth,
	increaseMonth,
	prevMonthButtonDisabled,
	nextMonthButtonDisabled
}: ReactDatePickerCustomHeaderProps) => {
	const { t, i18n } = useTranslation()

	return (
		<div className="flex items-center justify-between">
			<button
				type="button"
				aria-label={t('Previous month')}
				className={navButtonClassName}
				disabled={prevMonthButtonDisabled}
				onClick={decreaseMonth}>
				<ChevronLeftIcon />
			</button>
			<span className="select-none text-sm font-medium capitalize text-foreground">
				{format(date, 'LLLL yyyy', { locale: getDateLocale(i18n.language) })}
			</span>
			<button
				type="button"
				aria-label={t('Next month')}
				className={navButtonClassName}
				disabled={nextMonthButtonDisabled}
				onClick={increaseMonth}>
				<ChevronRightIcon />
			</button>
		</div>
	)
}

const CalendarPopper = ({ children }: PropsWithChildren) => (
	<div className="absolute left-0 top-0 z-10">{children}</div>
)

export const calendarProps = {
	calendarStartDay: 1,
	calendarContainer: CalendarContainer,
	dateFormat: DATE_FORMAT,
	popperPlacement: 'bottom-start',
	popperClassName: 'date-picker-popper',
	popperContainer: CalendarPopper,
	shouldCloseOnSelect: false,
	showPopperArrow: false,
	wrapperClassName: 'date-picker-wrapper peer',
	renderCustomHeader: (props: ReactDatePickerCustomHeaderProps) => <CalendarHeader {...props} />,
	renderDayContents: (day: number) => <span className="date-picker__day">{day}</span>
} as const
